import { parsePhoneNumberFromString } from 'libphonenumber-js';
import _ from 'lodash';
import moment from '@/utils/dates';
import enums from '../store/modules/enums';
import { resolveStatusColors, templates } from '../icons/icons_templates';
import Organization from '../models/Organization';
import type { WorkType } from '@/models/types';
import { SVG_STROKE_WIDTH } from '@/constants';
import { i18n } from '@/modules/i18n';
import type Incident from '@/models/Incident';

export function snakeToTitleCase(value: string) {
  if (!value) return '';
  // Ref: https://gist.github.com/kkiernan/91298079d34f0f832054
  return value
    .split('_')
    .map(function (item) {
      return item.charAt(0).toUpperCase() + item.slice(1);
    })
    .join(' ');
}

export function getWorkTypeName(workType: string) {
  const wType = enums.state.workTypes.find((type) => type.key === workType);
  return wType && wType.name_t ? i18n.global.t(wType.name_t) : '';
}

export function getStatusName(statusKey: string) {
  const status = enums.state.statuses.find((type) => type.status === statusKey);
  return status ? i18n.global.t(status.status_name_t) : '';
}

// getRecurrenceString used to live here but pulled rrule (~200 KB) onto every
// route that imports @/filters. The helper now lives in @/utils/rrule so only
// the components that actually render recurrence strings pay the cost.

export function formatDateString(date: string, format: string) {
  return moment(date).format(format);
}

export function formatRecurrence(date: string) {
  return formatDateString(date, 'ddd MMMM Do YYYY [at] h:mm A');
}

export function momentFromNow(date: string | moment.Dayjs) {
  return moment(date).fromNow();
}

export function getOrganizationName(id: string) {
  const organization = Organization.find(id);
  return organization ? organization.name : '';
}

export function getStatusBadge(status: string) {
  const statusDict: Record<string, string> = {
    open_unassigned: 'error',
    open_assigned: 'processing',
    'open_partially-completed': 'processing',
    'open_needs-follow-up': 'processing',
    open_unresponsive: 'default',
    closed_completed: 'success',
    'closed_partially-completed': 'success',
    closed_incomplete: 'default',
    'closed_out-of-scope': 'default',
    'closed_done-by-others': 'success',
    'closed_no-help-wanted': 'default',
    closed_rejected: 'default',
    closed_duplicate: 'default',
    'closed_marked-for-deletion': 'default',
  };
  return statusDict[status];
}

export const getColorForWorkType = (workType: WorkType) => {
  if (!workType) {
    return '';
  }
  return resolveStatusColors(workType.status, Boolean(workType.claimed_by))
    .fill;
};

export const getColorForStatus = (status: string, claimed = true) => {
  return resolveStatusColors(status, claimed).fill;
};

export const getWorkTypeImage = (workType: WorkType) => {
  const worksiteTemplate = templates[workType.work_type] || templates.unknown;
  const { fill, stroke } = resolveStatusColors(
    workType.status,
    Boolean(workType.claimed_by),
  );
  return worksiteTemplate
    .replaceAll('{{fillColor}}', fill)
    .replaceAll('{{strokeWidth}}', SVG_STROKE_WIDTH.toString())
    .replaceAll('{{strokeColor}}', stroke)
    .replaceAll('{{multiple}}', '');
};

export const secondsToHm = (seconds: number) => {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);

  const hDisplay = `${h}h `;
  const mDisplay = `${m}m`;
  return hDisplay + mDisplay;
};

export const formatSeconds = (seconds: number) => {
  return moment
    .utc(moment.duration(seconds, 's').asMilliseconds())
    .format('hh:mm:ss');
};

export const capitalize = (value: string) => {
  // "two words" -> "Two Words"
  if (!value) return '';
  const casted = value.toString().toLowerCase();
  const words = casted.split(' ');
  const cappedWords = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  const final = cappedWords.join(' ');
  return final;
};

export const toUpper = (value: string) => {
  if (!value) return '';
  return value.toUpperCase();
};

export const truncateFilter = (value: string, length: number, ...args: any) =>
  _.truncate(value, { length, ...args });

export const startCase = (value: string) => _.startCase(value);

export const snakeCase = (value: string) => _.snakeCase(value);

export const formatNationalNumber = (mobile: string) => {
  // Returns undefined instead of throwing on text that is not a phone number.
  const _number = parsePhoneNumberFromString(mobile, 'US');
  if (_number) {
    return _number.formatNational();
  }

  return mobile;
};

/**
 * Filter function for incidents with active phone numbers.
 * @param phone
 */
export function isValidActiveHotline(phone: unknown) {
  return Array.isArray(phone) ? phone.length > 0 : Boolean(phone);
}

export function formatIncidentPhoneNumbers<
  T extends { active_phone_number: Incident['active_phone_number'] },
>(incident: T): string[] {
  if (Array.isArray(incident.active_phone_number)) {
    return incident.active_phone_number.map((number) =>
      formatNationalNumber(String(number)),
    );
  }
  return [formatNationalNumber(String(incident.active_phone_number))];
}

/**
 * Get phone number from incident object
 * @param incident
 */
export function getIncidentPhoneNumbers<
  T extends { active_phone_number: Incident['active_phone_number'] },
>(incident: T): string {
  const formattedNumbers = formatIncidentPhoneNumbers(incident);
  return formattedNumbers.join(', ');
}
